import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, CreditCard, Download, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { API, errMsg, rm } from './careApi';

export default function CarePublicPay() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get('session_id');
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(sessionId ? { state: 'checking' } : null);
  const tries = useRef(0);

  const load = () => axios.get(`${API}/public/pay/${token}`).then((r) => setInv(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sessionId) return undefined;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/public/pay/${token}/status/${sessionId}`);
        if (data.payment_status === 'paid') { setCheck({ state: 'paid', receipt: data.receipt_no }); load(); return; }
        if (data.status === 'expired') { setCheck({ state: 'failed' }); return; }
      } catch (e) { if (e.response?.status === 404) { setCheck({ state: 'invalid' }); return; } }
      tries.current += 1;
      if (tries.current < 15) timer = setTimeout(poll, 2000); else setCheck({ state: 'pending' });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/public/pay/${token}/checkout`, { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  const receipt = async () => {
    const r = await axios.get(`${API}/public/pay/${token}/receipt/${sessionId}`, { responseType: 'blob' });
    window.open(URL.createObjectURL(r.data), '_blank');
  };

  if (err && !inv) return <div className="min-h-screen flex items-center justify-center p-6 text-slate-600" data-testid="pay-error">{err}</div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-700" /></div>;
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="care-public-pay">
      <div className="max-w-lg mx-auto">
        <p className="text-center text-2xl font-extrabold text-[#181B30]">{inv.company.name}</p>
        <p className="text-center text-xs text-slate-500 mt-1">{[inv.company.legal_name, inv.company.address].filter(Boolean).join(' · ')}</p>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6">
          {check?.state === 'checking' && <p className="flex items-center gap-2 text-sm text-slate-600 mb-4" data-testid="pay-checking"><Loader2 className="w-4 h-4 animate-spin" />Verifying your payment with the payment gateway…</p>}
          {check?.state === 'paid' && (
            <div className="rounded-xl bg-emerald-50 p-4 mb-4" data-testid="pay-success">
              <p className="font-semibold text-emerald-800 flex items-center gap-2"><CheckCircle2 className="w-5 h-5" />Payment verified. Thank you!</p>
              {check.receipt && <button onClick={receipt} className="mt-2 text-sm text-emerald-800 underline inline-flex items-center gap-1" data-testid="pay-download-receipt"><Download className="w-4 h-4" />Download receipt {check.receipt}</button>}
            </div>
          )}
          {check?.state === 'pending' && <p className="text-sm text-amber-700 mb-4" data-testid="pay-pending">We could not confirm the payment yet. If you were charged, your receipt will be issued once the gateway confirms.</p>}
          {check?.state === 'failed' && <p className="text-sm text-rose-700 mb-4">The payment was not completed. Please try again.</p>}
          {check?.state === 'invalid' && <p className="text-sm text-rose-700 mb-4" data-testid="pay-invalid-session">This payment session is not valid for this invoice.</p>}
          <div className="flex justify-between text-sm"><span className="text-slate-500">Invoice</span><b data-testid="pay-invoice-no">{inv.invoice_no}</b></div>
          <div className="flex justify-between text-sm mt-1"><span className="text-slate-500">Resident</span><span>{inv.resident_name} ({inv.resident_code})</span></div>
          <div className="flex justify-between text-sm mt-1"><span className="text-slate-500">Period</span><span>{inv.period_label}</span></div>
          <div className="flex justify-between text-sm mt-1"><span className="text-slate-500">Due date</span><span>{inv.due_date}</span></div>
          <div className="border-t border-slate-100 my-4" />
          {inv.items.map((i, k) => <div key={k} className="flex justify-between text-sm py-1"><span>{i.label}</span><span className="tabular-nums">{rm(i.amount)}</span></div>)}
          <div className="flex justify-between text-sm mt-3"><span>Total</span><span className="tabular-nums">{rm(inv.total)}</span></div>
          <div className="flex justify-between text-sm"><span>Paid</span><span className="tabular-nums">{rm(inv.amount_paid)}</span></div>
          <div className="flex justify-between text-lg font-bold mt-2"><span>Amount due</span><span className="tabular-nums" data-testid="pay-balance">{rm(inv.balance)}</span></div>
          {inv.balance > 0 && inv.status !== 'Cancelled' ? (
            <button onClick={pay} disabled={busy} data-testid="pay-now-btn"
              className="w-full mt-6 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}Pay {rm(inv.balance)} now
            </button>
          ) : <p className="mt-6 text-center text-sm font-semibold text-emerald-700" data-testid="pay-settled">{inv.status === 'Cancelled' ? 'This invoice has been cancelled.' : 'This invoice is fully paid.'}</p>}
          {err && <p className="text-sm text-rose-600 mt-3">{err}</p>}
          <p className="text-[11px] text-slate-400 mt-4 flex items-center gap-1 justify-center"><ShieldCheck className="w-3 h-3" />Secure payment by Stripe · Powered by StayMii Care</p>
        </div>
      </div>
    </div>
  );
}
